import { rowKey } from './rowAnchor'
import { cellKey } from './cellAnchor'
import { type RequestComment } from './revisionRequest'

// Review cell comments grouped for the Plan changes tab: by row (one thread
// strip under each row) and by cell (the count marker on the number).
export function groupByRow(comments: RequestComment[]): Map<string, RequestComment[]> {
  const map = new Map<string, RequestComment[]>()
  comments.forEach((c) => {
    if (!c.row_id) return
    const key = rowKey({ section: c.section, anchor_id: c.row_id })
    map.set(key, [...(map.get(key) ?? []), c])
  })
  return map
}

export function countByCell(comments: RequestComment[]): Map<string, number> {
  const map = new Map<string, number>()
  comments.forEach((c) => {
    if (!c.row_id || !c.column_key) return
    const key = cellKey({ section: c.section, anchor_id: c.row_id, column_key: c.column_key })
    map.set(key, (map.get(key) ?? 0) + 1)
  })
  return map
}

// A cell to open from the Conversation tab: its row on Plan changes, the
// cell's column and its name for the tab.
export interface CellLink { row_key: string; column_key: string; label: string }

export function cellLinkFor(c: RequestComment): CellLink | null {
  if (!c.row_id || !c.column_key) return null
  return { row_key: rowKey({ section: c.section, anchor_id: c.row_id }), column_key: c.column_key, label: c.cell_label ?? '' }
}

// One tab per commented cell of a row, in the order the cells were first
// commented on. `label` is the cell's name ("Halio × Shopee").
export interface CellTab { column_key: string; label: string; comments: RequestComment[] }

export function tabsByCell(rowComments: RequestComment[]): CellTab[] {
  const tabs = new Map<string, CellTab>()
  rowComments.forEach((c) => {
    if (!c.column_key) return
    const tab = tabs.get(c.column_key) ?? { column_key: c.column_key, label: c.cell_label ?? '', comments: [] }
    tab.comments.push(c)
    tabs.set(c.column_key, tab)
  })
  return [...tabs.values()]
}

// The tab that opens first: the cell with the newest comment.
export function newestTab(tabs: CellTab[]): CellTab | null {
  let best: CellTab | null = null
  let bestAt = ''
  tabs.forEach((t) => t.comments.forEach((c) => {
    if (c.at > bestAt) { best = t; bestAt = c.at }
  }))
  return best
}
